import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Video } from "lucide-react";
import { z } from "zod";
import {
  getUserRoles,
  getApprovalStatus,
  getRedirectPath,
  getAuthErrorMessage,
  verifyProfileExists,
} from "@/lib/auth-utils";
import SignupCompleteCard from "@/components/SignupCompleteCard";
import ForgotPasswordCard from "@/components/ForgotPasswordCard";
import SignupFields, { SignupFieldValues } from "@/components/SignupFields";

const emailSchema = z.string().trim().email("Invalid email address");
const passwordSchema = z.string().min(6, "Password must be at least 6 characters");
const phoneSchema = z.string().trim().regex(/^\d{10}$/, "Phone must be 10 digits");
const zipSchema = z.string().trim().regex(/^\d{5}$/, "Zip code must be 5 digits");
const nameSchema = z.string().trim().min(1, "This field is required").max(100);
const addressSchema = z.string().trim().min(1, "This field is required").max(200);

const emptySignupFields: SignupFieldValues = {
  firstName: "",
  lastName: "",
  username: "",
  phone: "",
  streetAddress: "",
  city: "",
  state: "",
  zipCode: "",
};

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [signupFields, setSignupFields] = useState<SignupFieldValues>(emptySignupFields);
  const [loading, setLoading] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const updateSignupFields = (patch: Partial<SignupFieldValues>) =>
    setSignupFields((prev) => ({ ...prev, ...patch }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Validate input
    const emailValidation = emailSchema.safeParse(email);
    const passwordValidation = passwordSchema.safeParse(password);
    if (!emailValidation.success) {
      toast({
        title: "Invalid email",
        description: emailValidation.error.issues[0].message,
        variant: "destructive",
      });
      setLoading(false);
      return;
    }
    if (!passwordValidation.success) {
      toast({
        title: "Invalid password",
        description: passwordValidation.error.issues[0].message,
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    // Additional validation for signup
    if (!isLogin) {
      const { firstName, lastName, phone, streetAddress, city, state, zipCode } = signupFields;
      const firstNameValidation = nameSchema.safeParse(firstName);
      const lastNameValidation = nameSchema.safeParse(lastName);
      const phoneValidation = phoneSchema.safeParse(phone);
      const streetAddressValidation = addressSchema.safeParse(streetAddress);
      const cityValidation = nameSchema.safeParse(city);
      const stateValidation = z.string().trim().length(2, "State must be 2 letters").safeParse(state);
      const zipCodeValidation = zipSchema.safeParse(zipCode);
      if (!firstNameValidation.success) {
        toast({
          title: "Invalid first name",
          description: firstNameValidation.error.issues[0].message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      if (!lastNameValidation.success) {
        toast({
          title: "Invalid last name",
          description: lastNameValidation.error.issues[0].message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      if (!phoneValidation.success) {
        toast({
          title: "Invalid phone number",
          description: phoneValidation.error.issues[0].message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      if (!streetAddressValidation.success) {
        toast({
          title: "Invalid street address",
          description: streetAddressValidation.error.issues[0].message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      if (!cityValidation.success) {
        toast({
          title: "Invalid city",
          description: cityValidation.error.issues[0].message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      if (!stateValidation.success) {
        toast({
          title: "Invalid state",
          description: stateValidation.error.issues[0].message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
      if (!zipCodeValidation.success) {
        toast({
          title: "Invalid zip code",
          description: zipCodeValidation.error.issues[0].message,
          variant: "destructive",
        });
        setLoading(false);
        return;
      }
    }
    try {
      if (isLogin) {
        // Sign in with password
        const { data, error } = await supabase.auth.signInWithPassword({
          email: emailValidation.data,
          password: passwordValidation.data,
        });

        if (error) {
          throw new Error(getAuthErrorMessage(error));
        }

        if (!data.user) {
          throw new Error("Sign in failed. Please try again.");
        }

        // Verify profile exists
        const profileExists = await verifyProfileExists(data.user.id);
        if (!profileExists) {
          console.error("Profile not found for user:", data.user.id);
          toast({
            title: "Account setup incomplete",
            description: "Please contact support. Your profile was not created properly.",
            variant: "destructive",
          });
          await supabase.auth.signOut();
          setLoading(false);
          return;
        }

        // Fetch user role and approval status
        const [userRoles, approvalStatus] = await Promise.all([
          getUserRoles(data.user.id),
          getApprovalStatus(data.user.id),
        ]);

        toast({
          title: "Welcome back!",
          description: "You've successfully signed in.",
        });

        // Determine redirect based on role and approval status
        const redirectPath = getRedirectPath(userRoles, approvalStatus);
        navigate(redirectPath);
      } else {
        // Sign up
        const { data, error } = await supabase.auth.signUp({
          email: emailValidation.data,
          password: passwordValidation.data,
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: {
              username: signupFields.username || email.split("@")[0],
              first_name: signupFields.firstName,
              last_name: signupFields.lastName,
              phone: signupFields.phone,
              street_address: signupFields.streetAddress,
              city: signupFields.city,
              state: signupFields.state,
              zip_code: signupFields.zipCode,
            },
          },
        });

        if (error) {
          throw new Error(getAuthErrorMessage(error));
        }

        // Check if email confirmation is required
        if (data.user && !data.session) {
          // Email confirmation required
          setSignupEmail(emailValidation.data);
          setSignupComplete(true);
          toast({
            title: "Account created!",
            description: "Please check your email to verify your account.",
            duration: 8000,
          });
        } else if (data.user && data.session) {
          // Auto-confirmed (shouldn't happen with Supabase default settings)
          // Verify profile was created
          const profileExists = await verifyProfileExists(data.user.id);

          if (!profileExists) {
            console.error("Profile creation failed for user:", data.user.id);
            toast({
              title: "Account setup incomplete",
              description: "Please try signing up again or contact support.",
              variant: "destructive",
            });
            await supabase.auth.signOut();
            setLoading(false);
            return;
          }

          toast({
            title: "Account created!",
            description: "Your account is pending approval.",
          });
          navigate("/");
        }
      }
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: getAuthErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (signupComplete) {
    return (
      <SignupCompleteCard
        signupEmail={signupEmail}
        onBackToSignIn={() => {
          setSignupComplete(false);
          setIsLogin(true);
        }}
      />
    );
  }

  if (showForgotPassword) {
    return <ForgotPasswordCard onBack={() => setShowForgotPassword(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 bg-gradient-primary rounded-lg flex items-center justify-center">
            <Video className="w-7 h-7" />
          </div>
          <span className="text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">
            Bridge Street Auctions
          </span>
        </div>

        <h1 className="text-2xl font-bold mb-2 text-center">
          {isLogin ? "Welcome Back" : "Create Account"}
        </h1>
        <p className="text-muted-foreground mb-6 text-center">
          {isLogin ? "Sign in to continue to Bridge Street Auctions" : "Join Bridge Street Auctions"}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && <SignupFields values={signupFields} onChange={updateSignupFields} />}

          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="password">Password *</Label>
              {isLogin && (
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-xs text-muted-foreground hover:text-primary transition-colors"
                >
                  Forgot password?
                </button>
              )}
            </div>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1"
            />
          </div>

          <Button type="submit" variant="hero" className="w-full" disabled={loading}>
            {loading ? "Loading..." : isLogin ? "Sign In" : "Sign Up"}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
          </button>
        </div>
      </Card>
    </div>
  );
};

export default Auth;
